import { memo, useState, useCallback, useMemo, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { FaPlus } from "react-icons/fa";
import RadioStationRow, { rowVariants } from "./RadioStationRow";
import NowPlayingBar from "./NowPlayingBar";
import CustomStationModal from "./CustomStationModal";
import type { RadioStation } from "./types";
import thumbnail from "../../assets/images/bg.webp";

const listVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07, delayChildren: 0.15 } },
};

// First 3 are finite-duration MP3s (seek works) — rest are live SomaFM streams
const STATIONS: RadioStation[] = [
  {
    id: "midnight-drive",
    name: "Midnight Drive",
    type: "Synthwave / Retrowave",
    frequency: "88.1 FM",
    thumbnail,
    streamUrl: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3",
  },
  {
    id: "neon-city",
    name: "Neon City",
    type: "Electronic / Chillout",
    frequency: "91.5 FM",
    thumbnail,
    streamUrl: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-2.mp3",
  },
  {
    id: "urban-pulse",
    name: "Urban Pulse",
    type: "Electronic / Dance",
    frequency: "94.3 FM",
    thumbnail,
    streamUrl: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-3.mp3",
  },
  {
    id: "groove-salad",
    name: "Groove Salad",
    type: "Electronic / Ambient · LIVE",
    frequency: "97.4 FM",
    thumbnail,
    streamUrl: "https://ice6.somafm.com/groovesalad-128-mp3",
  },
  {
    id: "illstreet",
    name: "Illinois Street Lounge",
    type: "Jazz / Lounge · LIVE",
    frequency: "102.5 FM",
    thumbnail,
    streamUrl: "https://ice2.somafm.com/illstreet-128-mp3",
  },
  {
    id: "metal",
    name: "Metal Detector",
    type: "Heavy Metal · LIVE",
    frequency: "105.1 FM",
    thumbnail,
    streamUrl: "https://ice2.somafm.com/metal-128-mp3",
  },
  {
    id: "drone-zone",
    name: "Drone Zone",
    type: "Ambient / Space · LIVE",
    frequency: "91.0 FM",
    thumbnail,
    streamUrl: "https://ice4.somafm.com/dronezone-128-mp3",
  },
  {
    id: "boot-liquor",
    name: "Boot Liquor",
    type: "Country / Western · LIVE",
    frequency: "107.8 FM",
    thumbnail,
    streamUrl: "https://ice1.somafm.com/bootliquor-128-mp3",
  },
];

interface Props {
  className?: string;
}

const CarRadio = memo<Props>(({ className = "" }) => {
  const [activeId, setActiveId] = useState<string>(STATIONS[0].id);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(85);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [customStation, setCustomStation] = useState<RadioStation | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  const audioRef = useRef<HTMLAudioElement | null>(null);

  const allStations = useMemo(
    () => (customStation ? [...STATIONS, customStation] : STATIONS),
    [customStation],
  );

  // Create audio element once
  useEffect(() => {
    const audio = new Audio();
    audio.preload = "none";
    audioRef.current = audio;

    const onTimeUpdate = () => setCurrentTime(Math.floor(audio.currentTime));
    const onDurationChange = () => {
      if (isFinite(audio.duration)) setDuration(Math.floor(audio.duration));
    };
    const onEnded = () => setIsPlaying(false);
    const onError = () => {
      setIsPlaying(false);
      setModalError(
        "Could not connect to this station. Check the URL and try again.",
      );
      setIsModalOpen(true);
    };

    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("durationchange", onDurationChange);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);

    return () => {
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("durationchange", onDurationChange);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
      audio.pause();
      audio.src = "";
    };
  }, []);

  // Swap stream when active station changes
  useEffect(() => {
    const audio = audioRef.current;
    const station = allStations.find((s) => s.id === activeId);
    if (!audio || !station) return;

    setCurrentTime(0);
    setDuration(0);
    audio.src = station.streamUrl;
    audio.volume = volume / 100;

    if (isPlaying) {
      audio.play().catch(() => setIsPlaying(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  // Play / pause
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      if (!audio.src) {
        const station = allStations.find((s) => s.id === activeId);
        if (station) audio.src = station.streamUrl;
      }
      audio.play().catch(() => setIsPlaying(false));
    } else {
      audio.pause();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, activeId]);

  // Volume
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume / 100;
  }, [volume]);

  const activeStation = useMemo(
    () => allStations.find((s) => s.id === activeId) ?? null,
    [allStations, activeId],
  );

  const isLive = duration === 0;

  const handleSeek = useCallback(
    (time: number) => {
      setCurrentTime(time);
      if (audioRef.current && isFinite(duration) && duration > 0) {
        audioRef.current.currentTime = time;
      }
    },
    [duration],
  );

  const toggleFavorite = useCallback((id: string) => {
    setFavorites((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const handleSelect = useCallback((id: string) => {
    setActiveId(id);
    setIsPlaying(true);
  }, []);

  const handlePrev = useCallback(() => {
    setActiveId((prev) => {
      const idx = allStations.findIndex((s) => s.id === prev);
      return allStations[(idx - 1 + allStations.length) % allStations.length]
        .id;
    });
  }, [allStations]);

  const handleNext = useCallback(() => {
    setActiveId((prev) => {
      const idx = allStations.findIndex((s) => s.id === prev);
      return allStations[(idx + 1) % allStations.length].id;
    });
  }, [allStations]);

  const handleCustomPlay = useCallback(
    (input: string) => {
      setModalError(null);

      // Frequency lookup — matches "84.6", "84.6 FM", "84.6FM"
      const freqMatch = input.match(/^(\d+\.?\d*)\s*(FM|AM)?$/i);
      if (freqMatch) {
        const num = freqMatch[1];
        const found = STATIONS.find(
          (s) =>
            s.frequency.startsWith(num + " ") ||
            s.frequency.startsWith(num + "F"),
        );
        if (found) {
          handleSelect(found.id);
          setIsModalOpen(false);
          return;
        }
        setModalError(
          `No station found at ${num} FM. Try a stream URL instead.`,
        );
        return;
      }

      // URL validation
      if (!input.startsWith("http://") && !input.startsWith("https://")) {
        setModalError(
          "Enter a valid stream URL (https://...) or frequency (e.g. 84.6).",
        );
        return;
      }

      const station: RadioStation = {
        id: "custom",
        name: "Custom Station",
        type: "Custom Stream",
        frequency: "— FM",
        thumbnail,
        streamUrl: input,
      };

      setCustomStation(station);
      setActiveId("custom");
      setIsPlaying(true);
      setIsModalOpen(false);
    },
    [handleSelect],
  );

  const stationRows = useMemo(
    () =>
      allStations.map((station) => (
        <RadioStationRow
          key={station.id}
          station={station}
          isActive={station.id === activeId}
          isFavorite={favorites.has(station.id)}
          onSelect={handleSelect}
          onFavoriteToggle={toggleFavorite}
        />
      )),
    [allStations, activeId, favorites, handleSelect, toggleFavorite],
  );

  return (
    <div
      className={`flex-1 min-h-0 flex flex-col gap-3 pt-4 ${className} relative`}
    >
      {/* Section header */}
      <motion.div
        className="flex-shrink-0 border-b border-white/5 pb-4 flex items-center justify-between"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
      >
        <div>
          <h2 className="text-white text-[20px] font-[400] leading-tight">
            Radio Stations
          </h2>
          <p className="text-white/50 text-[13px] mt-0.5">
            Huge list of cool stations
          </p>
        </div>

        <motion.button
          onClick={() => {
            setModalError(null);
            setIsModalOpen(true);
          }}
          whileTap={{ scale: 0.88 }}
          whileHover={{ scale: 1.06 }}
          title="Enter custom station"
          className="flex items-center gap-1.5 px-3 py-[6px] rounded-sm bg-[#137FE3]/10 border border-[#137FE3]/30 text-[#137FE3] hover:bg-[#137FE3]/20 hover:border-[#137FE3]/60 transition-colors text-[12px]"
        >
          <FaPlus size={10} />
          <span>Enter manually</span>
        </motion.button>
      </motion.div>

      {/* Station list */}
      <motion.div
        className="flex-1 min-h-0 overflow-y-auto scrollbar-hide space-y-1"
        variants={listVariants}
        initial="hidden"
        animate="show"
      >
        {stationRows}
      </motion.div>

      {/* Now Playing */}
      <div className="flex-shrink-0">
        <NowPlayingBar
          station={activeStation}
          isPlaying={isPlaying}
          isLive={isLive}
          volume={volume}
          currentTime={currentTime}
          duration={duration}
          onPlayPause={() => setIsPlaying((p) => !p)}
          onPrev={handlePrev}
          onNext={handleNext}
          onVolumeChange={setVolume}
          onSeek={handleSeek}
        />
      </div>

      {/* Custom station modal */}
      <CustomStationModal
        isOpen={isModalOpen}
        error={modalError}
        onClose={() => setIsModalOpen(false)}
        onPlay={handleCustomPlay}
      />
    </div>
  );
});

CarRadio.displayName = "CarRadio";
export default CarRadio;
